import React, { useState, useEffect, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, rad, type, icBox, secH, listRow } from "../../constants/styles";
import Ic from "../common/Ic";
import { formatPlanLabel } from "../../utils/plans";

export default function AdminDash() {
  const { t, h, go, setSelOrgId, flash } = useContext(Ctx);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgSearch, setOrgSearch] = useState("");
  const [showAllOrgs, setShowAllOrgs] = useState(false);
  const [expandedBill, setExpandedBill] = useState(null);
  // Recent Billing pagination — same 5-then-show-more pattern used
  // by the Organizations list above. Keeps the AdminDash scrollable
  // length sane when the table has 50+ rows.
  const [showAllBills, setShowAllBills] = useState(false);
  // Revenue card period: this_month (default) | last_month | custom | all
  const [revPeriod, setRevPeriod] = useState("this_month");
  const [revFrom, setRevFrom] = useState("");
  const [revTo, setRevTo] = useState("");
  const [revData, setRevData] = useState(null);
  const [revLoading, setRevLoading] = useState(false);
  // Drill-down list shown when an admin taps a revenue bucket amount.
  const [revList, setRevList] = useState(null); // null | { bucket, loading } | { bucket, items }
  const openRevList = async (bucket) => {
    setRevList({ bucket, loading: true });
    try {
      const params = { period: revPeriod, bucket };
      if (revPeriod === "custom") { params.from = revFrom; params.to = revTo; }
      const res = await api.adminGetRevenueList(params);
      setRevList({ bucket, items: res.items || [] });
    } catch (e) { setRevList({ bucket, items: [] }); }
  };
  // Trends card data (last 6 months by default)
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.adminGetStats();
        if (res.success) setStats(res.stats);
      } catch (e) {
        console.error("Admin load error:", e);
        flash(t.loginFail || "Failed to load", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Reload revenue when period (or custom range) changes. Custom only
  // fires when both ends are set so we don't hammer the API on each
  // keystroke while the user is still picking dates.
  useEffect(() => {
    const load = async () => {
      if (revPeriod === "custom" && (!revFrom || !revTo)) return;
      setRevLoading(true);
      try {
        const params = { period: revPeriod };
        if (revPeriod === "custom") { params.from = revFrom; params.to = revTo; }
        const res = await api.adminGetRevenue(params);
        if (res.success) setRevData(res);
      } catch (e) {
        console.error("Revenue load error:", e);
      } finally {
        setRevLoading(false);
      }
    };
    load();
  }, [revPeriod, revFrom, revTo]);

  // Trends card — fetched once on mount.
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.adminGetTrends(6);
        if (res.success) setTrends(res);
      } catch (e) {
        console.error("Trends load error:", e);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div style={{ paddingTop: 40, textAlign: "center" }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
      <p style={{ color: h.ts, ...type.sm, marginTop: sp.md }}>{t.connecting || "Loading..."}</p>
    </div>;
  }

  // Each metric card deep-links to its respective admin page.
  // Payments lands on adminReports because that's where billing /
  // payment data lives in admin tooling — there's no separate
  // AdminPayments view today, and Reports already exposes the
  // billing transactions table for export.
  const primaryStats = stats ? [
    { label: "Organizations", value: stats.totalOrgs, icon: "building", color: h.ac, page: "adminOrgs" },
    { label: "Properties", value: stats.totalProps, icon: "home", color: h.gn, page: "adminProperties" },
    { label: "Tenants", value: stats.totalTenants, icon: "users", color: h.pp || h.ac, page: "adminTenants" },
    { label: "Payments", value: stats.totalPayments, icon: "credit-card", color: h.wn, page: "adminReports" },
  ] : [];

  const secondaryStats = stats ? [
    { label: "Service Persons", value: stats.totalServicePersons, icon: "wrench" },
    { label: "Leads", value: stats.totalLeads, icon: "target" },
    { label: "Leases", value: stats.totalLeases, icon: "file-text" },
    { label: "Active Orgs", value: stats.activeOrgs, icon: "check-circle" },
  ] : [];

  const navItems = [
    { label: "Organizations", desc: `${stats?.totalOrgs || 0} total`, icon: "building", page: "adminOrgs", color: h.ac },
    { label: "Properties", desc: `${stats?.totalProps || 0} total`, icon: "home", page: "adminProperties", color: h.gn },
    { label: "Tenants", desc: `${stats?.totalTenants || 0} total`, icon: "users", page: "adminTenants", color: h.pp || h.ac },
    { label: "Service Persons", desc: `${stats?.totalServicePersons || 0} total`, icon: "wrench", page: "adminServicePersons", color: h.wn },
    // Pricing & Plans is now a unified screen: the pricing-mode
    // toggle inside it swaps between Fixed-Tier and Per-Property
    // catalogs, so the separate "Per-Property Plan" tile is no
    // longer needed. The /adminPerPropertyPlan route stays around
    // for any external deep links.
    { label: "Pricing & Plans", desc: "Tiers + per-property", icon: "settings", page: "adminPricing", color: h.ac },
    { label: "Platform Reports", desc: "Export CSV reports", icon: "grid", page: "adminReports", color: h.gn },
    { label: "Feedback Inbox", desc: stats?.newFeedback > 0 ? `${stats.newFeedback} new reports` : "Bug reports & feedback", icon: "msg", page: "adminFeedback", color: "#ef4444", badge: stats?.newFeedback },
    { label: "🎉 Festival & Offers", desc: "Greetings + landlord discounts", icon: "bell", page: "adminCampaign", color: "#7c3aed" },
    // Phase 6: vacate dispute queue. Badge count comes from
    // stats.disputedVacates when present; falls back to "Resolve
    // tenant-landlord disputes" subline. The label gets a 🚩 prefix
    // so it stands out in the manage list.
    { label: "🚩 Vacate Disputes", desc: stats?.disputedVacates > 0 ? `${stats.disputedVacates} awaiting ruling` : "Tenant-landlord deposit disputes", icon: "alert-triangle", page: "adminVacateQueue", color: "#ef4444", badge: stats?.disputedVacates },
  ];

  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    {/* Header */}
    <div style={{ marginBottom: sp.xl }}>
      <h2 style={{ ...type.h1, color: h.tx, margin: 0 }}>Platform Overview</h2>
      <p style={{ ...type.sm, color: h.ts, marginTop: sp.xs }}>Super Admin Dashboard</p>
    </div>

    {/* Primary Stats Grid */}
    {stats && <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.lg }}>
        {primaryStats.map((s, i) => <div
          key={i}
          onClick={s.page ? () => go(s.page) : undefined}
          style={{
            ...crd(h),
            padding: sp.lg,
            display: "flex",
            alignItems: "center",
            gap: sp.md,
            cursor: s.page ? "pointer" : "default",
          }}
          // Light hover affordance so the cards look clickable without
          // adding a chevron that would crowd the compact card layout.
          onMouseEnter={s.page ? (e) => { e.currentTarget.style.borderColor = `${s.color}60`; } : undefined}
          onMouseLeave={s.page ? (e) => { e.currentTarget.style.borderColor = ''; } : undefined}
        >
          <div style={icBox(s.color, 40)}>
            <Ic n={s.icon} s={18} c={s.color} />
          </div>
          <div>
            <div style={{ ...type.h2, color: h.tx }}>{s.value}</div>
            <div style={{ ...type.xs, color: h.ts, marginTop: 2 }}>{s.label}</div>
          </div>
        </div>)}
      </div>

      {/* Secondary Stats Row */}
      <div style={{ ...crd(h), display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: sp.xs, padding: sp.md }}>
        {secondaryStats.map((s, i) => <div key={i} style={{ textAlign: "center", padding: sp.sm }}>
          <div style={{ ...type.h3, color: h.tx }}>{s.value}</div>
          <div style={{ fontSize: 9, fontWeight: 500, color: h.tt, marginTop: 2 }}>{s.label}</div>
        </div>)}
      </div>

      {/* Plan Distribution + Revenue */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginTop: sp.lg }}>
        <div style={{ ...crd(h), padding: sp.lg }}>
          <div style={{ ...type.cap, color: h.ts, marginBottom: sp.md }}>Plan Split</div>
          {stats.planSplit ? <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-around", alignItems: "center", gap: sp.sm }}>
            {Object.entries(stats.planSplit).map(([planId, info], i) => {
              const colors = { free: h.wn, starter: h.ac, growth: h.pp || "#8b5cf6", business: h.gn, pro: h.gn };
              const col = colors[planId] || h.ac;
              return <div key={planId} style={{ textAlign: "center", minWidth: 50 }}>
                <div style={{ ...type.h3, color: col }}>{info.count}</div>
                <span style={{ ...bdg(`${col}20`, col), fontSize: 9 }}>{info.name}</span>
              </div>;
            })}
          </div> : <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ ...type.h2, color: h.wn }}>{stats.freeOrgs}</div>
              <span style={bdg(`${h.wn}20`, h.wn)}>FREE</span>
            </div>
            <div style={{ width: 1, height: 30, background: h.bd }} />
            <div style={{ textAlign: "center" }}>
              <div style={{ ...type.h2, color: h.gn }}>{stats.proOrgs}</div>
              <span style={bdg(`${h.gn}20`, h.gn)}>PAID</span>
            </div>
          </div>}
        </div>
        <div style={{
          ...crd(h),
          padding: sp.lg,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "stretch",
        }}>
          <div style={{ ...type.cap, color: h.ts, marginBottom: sp.sm, textAlign: "center" }}>Revenue</div>
          {/* Period pills \u2014 compact so all four fit on a phone width */}
          <div style={{ display: "flex", gap: 4, justifyContent: "center", marginBottom: sp.sm, flexWrap: "wrap" }}>
            {[
              { id: "this_month", lbl: "This" },
              { id: "last_month", lbl: "Last" },
              { id: "custom", lbl: "Custom" },
              { id: "all", lbl: "All" },
            ].map(p => <button key={p.id}
              onClick={() => setRevPeriod(p.id)}
              style={{
                padding: "4px 8px",
                borderRadius: rad.sm,
                border: `1px solid ${revPeriod === p.id ? h.ac : h.bd}`,
                background: revPeriod === p.id ? `${h.ac}20` : "transparent",
                color: revPeriod === p.id ? h.ac : h.ts,
                fontSize: 10,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >{p.lbl}</button>)}
          </div>
          {revPeriod === "custom" && <div style={{ display: "flex", gap: 4, justifyContent: "center", marginBottom: sp.sm }}>
            <input type="date" value={revFrom} onChange={e => setRevFrom(e.target.value)} style={{ flex: 1, fontSize: 10, padding: 4, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.bg, color: h.tx, minWidth: 0 }} />
            <input type="date" value={revTo} onChange={e => setRevTo(e.target.value)} style={{ flex: 1, fontSize: 10, padding: 4, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.bg, color: h.tx, minWidth: 0 }} />
          </div>}
          {/* Paid + pending split. Falls back to legacy stats.totalRevenue
              before the first /stats/revenue call returns so the card
              never flashes a 0 on initial render. */}
          <div style={{ textAlign: "center" }}>
            <div onClick={() => openRevList("paid")} title="View paid orders" style={{ ...type.h2, color: h.gn, cursor: "pointer" }}>
              {"\u20B9"}{(revData?.paid ?? stats.totalRevenue ?? 0).toLocaleString("en-IN")}
            </div>
            <div style={{ ...type.xs, color: h.ts, marginTop: 2 }}>
              {revLoading ? "Loading..." : (revData?.label || "Paid")}
            </div>
            {(revData?.pending || 0) > 0 && <div onClick={() => openRevList("pending")} title="View pending orders" style={{ marginTop: sp.sm, padding: sp.xs, borderTop: `1px dashed ${h.bd}`, cursor: "pointer" }}>
              <div style={{ ...type.sm, color: h.wn, fontWeight: 700 }}>
                {"\u20B9"}{(revData.pending).toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize: 9, color: h.ts }}>
                Pending ({revData.pendingCount})
              </div>
            </div>}
            {(revData?.failed || 0) > 0 && <div onClick={() => openRevList("failed")} title="View failed orders" style={{ marginTop: sp.xs, padding: sp.xs, borderTop: `1px dashed ${h.bd}`, cursor: "pointer" }}>
              <div style={{ ...type.sm, color: h.rd, fontWeight: 700 }}>
                {"\u20B9"}{(revData.failed).toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize: 9, color: h.ts }}>
                Failed ({revData.failedCount})
              </div>
            </div>}
            {revList && <div onClick={() => setRevList(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: h.bg, borderRadius: 16, width: "100%", maxWidth: 420, maxHeight: "82vh", overflowY: "auto", padding: 18, textAlign: "left", boxShadow: "0 12px 48px rgba(0,0,0,0.25)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ color: h.tx, fontSize: 15, fontWeight: 700, textTransform: "capitalize" }}>{revList.bucket} orders{revList.items ? ` (${revList.items.length})` : ""}</span>
                  <button onClick={() => setRevList(null)} style={{ background: "none", border: "none", color: h.sc, fontSize: 16, cursor: "pointer" }}>{"×"}</button>
                </div>
                {revList.loading ? <div style={{ color: h.sc, fontSize: 12, padding: "12px 0" }}>Loading…</div>
                  : (revList.items || []).length === 0 ? <div style={{ color: h.sc, fontSize: 12, padding: "12px 0" }}>No orders in this bucket.</div>
                  : (revList.items || []).map(it => <div key={it.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 0", borderTop: `1px solid ${h.bd}` }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: h.tx, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.org}</div>
                      <div style={{ color: h.ts, fontSize: 10 }}>{(it.plan || "plan") + (it.cycle ? " · " + it.cycle : "") + (it.at ? " · " + String(it.at).slice(0, 10) : "")}</div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ color: h.tx, fontSize: 13, fontWeight: 700 }}>{"₹"}{(it.amount || 0).toLocaleString("en-IN")}</div>
                      <div style={{ fontSize: 9, color: h.ts }}>{it.status}</div>
                    </div>
                  </div>)}
              </div>
            </div>}
          </div>
        </div>
      </div>

      {/* Trends card \u2014 last 6 months. Three small SVG bar charts
          (revenue, new orgs, new tenants) plus the plan-share row
          highlighting which subscription has the most users. */}
      {trends && trends.months && trends.months.length > 0 && <div style={{ ...crd(h), padding: sp.lg, marginTop: sp.lg }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.md }}>
          <div style={{ ...type.cap, color: h.ts }}>Trends {"\u00B7"} Last 6 months</div>
          <Ic n="bar-chart" s={14} c={h.ts} />
        </div>
        {(() => {
          // Inline SVG mini-bar chart. Each row scales to its own max so
          // small series (new orgs) aren't dwarfed by large ones (revenue).
          const months = trends.months;
          const Bars = ({ values, labels, color, format }) => {
            const max = Math.max(1, ...values.map(v => Math.abs(v)));
            const W = 260, H = 40, n = values.length;
            const bw = (W / n) - 4;
            return <svg viewBox={`0 0 ${W} ${H + 14}`} width="100%" preserveAspectRatio="none" style={{ display: "block" }}>
              {values.map((v, i) => {
                const bh = Math.max(2, (Math.abs(v) / max) * H);
                return <g key={i}>
                  <rect x={i * (W / n) + 2} y={H - bh} width={bw} height={bh} fill={color} rx={2} />
                  <text x={i * (W / n) + bw / 2 + 2} y={H + 11} fill={h.ts} fontSize={8} textAnchor="middle">{labels[i]}</text>
                </g>;
              })}
              {format && <text x={W} y={10} fill={h.tx} fontSize={9} fontWeight={700} textAnchor="end">{format(values[values.length - 1])}</text>}
            </svg>;
          };
          const fmtINR = v => "\u20B9" + v.toLocaleString("en-IN");
          const lbls = months.map(m => m.label);
          // % change in tenants between the last two buckets \u2014 gives
          // a quick "growth or decline" read alongside the bars.
          const tenLast = months[months.length - 1]?.newTenants || 0;
          const tenPrev = months[months.length - 2]?.newTenants || 0;
          const tenDelta = tenPrev === 0 ? (tenLast > 0 ? 100 : 0) : Math.round(((tenLast - tenPrev) / tenPrev) * 100);
          const orgLast = months[months.length - 1]?.newOrgs || 0;
          const orgPrev = months[months.length - 2]?.newOrgs || 0;
          const orgDelta = orgPrev === 0 ? (orgLast > 0 ? 100 : 0) : Math.round(((orgLast - orgPrev) / orgPrev) * 100);
          return <>
            <div style={{ marginBottom: sp.md }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ ...type.xs, color: h.ts, fontWeight: 600 }}>Revenue</span>
                <span style={{ ...type.xs, color: h.gn, fontWeight: 700 }}>{fmtINR(months[months.length - 1].revenue)}</span>
              </div>
              <Bars values={months.map(m => m.revenue)} labels={lbls} color={h.gn} />
            </div>
            <div style={{ marginBottom: sp.md }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ ...type.xs, color: h.ts, fontWeight: 600 }}>New Orgs</span>
                <span style={{ ...type.xs, color: orgDelta >= 0 ? h.gn : "#ef4444", fontWeight: 700 }}>
                  {orgLast} ({orgDelta >= 0 ? "+" : ""}{orgDelta}%)
                </span>
              </div>
              <Bars values={months.map(m => m.newOrgs)} labels={lbls} color={h.ac} />
            </div>
            <div style={{ marginBottom: sp.md }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ ...type.xs, color: h.ts, fontWeight: 600 }}>New Tenants</span>
                <span style={{ ...type.xs, color: tenDelta >= 0 ? h.gn : "#ef4444", fontWeight: 700 }}>
                  {tenLast} ({tenDelta >= 0 ? "+" : ""}{tenDelta}%)
                </span>
              </div>
              <Bars values={months.map(m => m.newTenants)} labels={lbls} color={h.pp || "#8b5cf6"} />
            </div>
            {/* Plan leaderboard \u2014 highest count gets a "leader" pill */}
            {trends.planSplit && trends.planSplit.length > 0 && <div style={{ marginTop: sp.md, paddingTop: sp.md, borderTop: `1px solid ${h.bd}` }}>
              <div style={{ ...type.xs, color: h.ts, fontWeight: 600, marginBottom: sp.sm }}>Subscriptions by users</div>
              {trends.planSplit.map((p, i) => {
                const total = trends.planSplit.reduce((s, x) => s + x.count, 0) || 1;
                const pct = Math.round((p.count / total) * 100);
                const isLeader = i === 0;
                return <div key={p.id} style={{ marginBottom: sp.xs }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                    <span style={{ ...type.xs, color: h.tx, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                      {p.name}
                      {isLeader && <span style={{ ...bdg(`${h.gn}20`, h.gn), fontSize: 8, padding: "1px 5px" }}>Leader</span>}
                    </span>
                    <span style={{ ...type.xs, color: h.ts }}>{p.count} ({pct}%)</span>
                  </div>
                  <div style={{ height: 4, background: h.bd, borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: isLeader ? h.gn : h.ac, transition: "width 0.3s" }} />
                  </div>
                </div>;
              })}
            </div>}
          </>;
        })()}
      </div>}
    </>}

    {/* Quick Navigation */}
    <div style={{ marginTop: sp.xxl }}>
      <div style={secH(h)}>
        <Ic n="grid" s={16} c={h.ac} />
        Manage
      </div>
      <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
        {navItems.map((item, i) => (
          <div key={i}
            onClick={() => go(item.page)}
            style={{
              ...listRow(h, i === navItems.length - 1),
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
              <div style={icBox(item.color, 36)}>
                <Ic n={item.icon} s={16} c={item.color} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: sp.xs }}>
                  <div style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{item.label}</div>
                  {item.badge > 0 && <span style={{ background: "#ef4444", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{item.badge}</span>}
                </div>
                <div style={{ ...type.xs, color: h.tt }}>{item.desc}</div>
              </div>
            </div>
            <Ic n="chevron-right" s={16} c={h.tt} />
          </div>
        ))}
      </div>
    </div>

    {/* Organizations */}
    {stats?.recentOrgs?.length > 0 && <div style={{ marginTop: sp.xxl }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.md }}>
        <div style={secH(h)}>
          <Ic n="building" s={16} c={h.ac} />
          Organizations
        </div>
        <span onClick={() => go("adminOrgs")} style={{ ...type.xs, color: h.ac, cursor: "pointer", fontWeight: 500 }}>View All ({stats.totalOrgs})</span>
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: sp.md }}>
        <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
          <Ic n="search" s={14} c={h.tt} />
        </div>
        <input value={orgSearch} onChange={e => { setOrgSearch(e.target.value); setShowAllOrgs(false); }}
          placeholder="Search organization..."
          style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, paddingLeft: 34, borderRadius: rad.md, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, ...type.xs, outline: "none", boxSizing: "border-box" }}
        />
      </div>

      {(() => {
        const filtered = (stats.recentOrgs || []).filter(o => {
          if (!orgSearch) return true;
          const q = orgSearch.toLowerCase();
          return (o.name || "").toLowerCase().includes(q) || (o.owner_phone || "").includes(q) || (o.slug || "").toLowerCase().includes(q);
        });
        const visible = showAllOrgs ? filtered : filtered.slice(0, 5);
        const hasMore = filtered.length > 5 && !showAllOrgs;

        return <>
          {visible.length === 0 && <div style={{ ...crd(h), textAlign: "center", padding: sp.lg }}>
            <div style={{ ...type.xs, color: h.tt }}>No organizations match "{orgSearch}"</div>
          </div>}

          {visible.map(org => {
            const planColors = { free: h.wn, starter: h.ac, growth: h.pp || "#8b5cf6", business: h.gn, pro: h.gn };
            const pCol = planColors[org.plan] || h.ac;
            // Friendly label resolves: server plan_name → known
            // ids → title-cased fallback. Stops raw 'PER_PROPERTY_V1'
            // strings from leaking into the org badge.
            const pLabel = formatPlanLabel(org.plan, org.plan_name);
            return <div key={org.id}
              onClick={() => { setSelOrgId(org.id); go("adminOrgDetail"); }}
              style={{ ...crd(h), cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.xs }}>
                  <span style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{org.name}</span>
                  <span style={bdg(`${pCol}20`, pCol)}>{pLabel}</span>
                  {!org.active && <span style={bdg(`${h.rd}20`, h.rd)}>Inactive</span>}
                </div>
                <div style={{ ...type.xs, color: h.ts }}>
                  {org.owner_phone || "No phone"} {"\u00B7"} Created {org.created_at ? new Date(org.created_at).toLocaleDateString() : "—"}
                  {org.plan_end && <span> {"\u00B7"} Expires {new Date(org.plan_end).toLocaleDateString()}</span>}
                </div>
              </div>
              <Ic n="chevron-right" s={16} c={h.ac} />
            </div>;
          })}

          {hasMore && <div
            onClick={() => setShowAllOrgs(true)}
            style={{ textAlign: "center", padding: sp.md, cursor: "pointer" }}
          >
            <span style={{ ...type.xs, color: h.ac, fontWeight: 600 }}>Show More ({filtered.length - 5} more)</span>
          </div>}
        </>;
      })()}
    </div>}

    {/* Recent Billing */}
    {stats?.recentBilling?.length > 0 && <div style={{ marginTop: sp.xxl, marginBottom: sp.xxxl }}>
      <div style={secH(h)}>
        <Ic n="credit-card" s={16} c={h.gn} />
        Recent Billing
      </div>
      {(showAllBills ? stats.recentBilling : stats.recentBilling.slice(0, 5)).map((b, i) => {
        const ok = b.status === "captured" || b.status === "verified";
        const fail = b.status === "failed";
        const planLabel = formatPlanLabel(b.plan_type, b.plan_name);
        const planColors = { free: h.wn, starter: h.ac, growth: h.pp || "#8b5cf6", business: h.gn, pro: h.gn };
        const pCol = planColors[b.plan_type] || h.ac;
        const isExpanded = expandedBill === (b.id || i);
        const detailRow = (label, value) => value ? <div style={{ display: "flex", justifyContent: "space-between", padding: `${sp.xs}px 0`, borderBottom: `1px solid ${h.bd}` }}>
          <span style={{ ...type.xs, color: h.ts }}>{label}</span>
          <span style={{ ...type.xs, color: h.tx, fontWeight: 500, textAlign: "right", maxWidth: "60%", wordBreak: "break-all" }}>{value}</span>
        </div> : null;

        return <div key={b.id || i}
          onClick={() => setExpandedBill(isExpanded ? null : (b.id || i))}
          style={{ ...crd(h), cursor: "pointer", border: isExpanded ? `1.5px solid ${h.ac}30` : undefined }}
        >
          {/* Summary row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.xs }}>
                <span style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{"\u20B9"}{b.amount}</span>
                <span style={{ ...bdg(`${pCol}20`, pCol), fontSize: 9 }}>{planLabel}</span>
                <span style={bdg(
                  ok ? `${h.gn}20` : fail ? `${h.rd}20` : `${h.wn}20`,
                  ok ? h.gn : fail ? h.rd : h.wn
                )}>{ok ? "Paid" : fail ? "Failed" : b.status || "pending"}</span>
              </div>
              <div style={{ ...type.xs, color: h.ts }}>
                {b.org_name || b.org_id || "—"} {b.org_phone ? `(${b.org_phone})` : ""}
              </div>
            </div>
            <Ic n={isExpanded ? "chevron-up" : "chevron-down"} s={16} c={h.tt} />
          </div>

          {/* Expanded details */}
          {isExpanded && <div style={{ marginTop: sp.md, paddingTop: sp.md, borderTop: `1px solid ${h.bd}` }}>
            {detailRow("Organization", b.org_name && b.org_name !== b.org_id ? b.org_name : "—")}
            {detailRow("Org ID", b.org_id || "—")}
            {detailRow("Phone", b.org_phone)}
            {detailRow("Plan", planLabel)}
            {detailRow("Amount (Paise)", b.amount_paise?.toLocaleString())}
            {detailRow("Amount", `\u20B9${b.amount}`)}
            {detailRow("Currency", b.currency || "INR")}
            {detailRow("Status", ok ? "Verified / Paid" : fail ? "Failed" : b.status)}
            {detailRow("Payment Source", b.source || "—")}
            {detailRow("Order Date", b.created_at ? new Date(b.created_at).toLocaleString() : "—")}
            {detailRow("Verified At", b.verified_at ? new Date(b.verified_at).toLocaleString() : "Not verified")}
            {detailRow("Razorpay Order ID", b.razorpay_order_id)}
            {detailRow("Razorpay Payment ID", b.razorpay_payment_id)}
            {detailRow("Transaction ID", b.id)}
            {b.notes && detailRow("Notes", b.notes)}

            {/* Quick action — go to org */}
            {b.org_id && <div
              onClick={(e) => { e.stopPropagation(); setSelOrgId(b.org_id); go("adminOrgDetail"); }}
              style={{ marginTop: sp.md, textAlign: "center", padding: sp.sm, background: `${h.ac}10`, borderRadius: rad.md, cursor: "pointer" }}
            >
              <span style={{ ...type.xs, color: h.ac, fontWeight: 600 }}>View Organization →</span>
            </div>}
          </div>}
        </div>;
      })}

      {/* Show More — mirrors the Organizations list footer above so
          the AdminDash doesn't run on for 50+ billing rows. Once
          expanded there's no Show Less; reload the page to collapse. */}
      {stats.recentBilling.length > 5 && !showAllBills && <div
        onClick={() => setShowAllBills(true)}
        style={{ textAlign: "center", padding: sp.md, cursor: "pointer" }}
      >
        <span style={{ ...type.xs, color: h.ac, fontWeight: 600 }}>Show More ({stats.recentBilling.length - 5} more)</span>
      </div>}
    </div>}
  </div>;
}
